import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Checkbox, Tooltip } from 'antd';
import { CheckboxGroupSection, CheckboxWrapper, HorizontalLayout } from './styles';
import { CheckboxGroupSettings } from './CheckboxGroupSettings';
import { PageElementsContext } from '../../../../context/pageElementsContext';
import { ELEMENTS_SETTINGS_TOOLBAR } from '../../../../artboard/Toolbar/translationValues';
import { useTranslation } from 'react-i18next';
import { ColorButton } from '../../../../artboard/Toolbar/styles';
import ColorPicker from '../../../../artboard/ColorPicker';
import { deepCopy } from '../../../../utils/javascript';
import { debounce } from 'underscore';
import { TEXT_DEBOUNCE_TIME_OUT } from '../../../../utils/complexDocumentConstants';
import { CONTEXT_KEYS, useArtboardContext } from '../../../../context/complexArtboardContext';
import { FillableElementWrapper } from '../../styles';

const getCheckBoxClassName = checkType => {
  let additionClassName = '';
  switch (checkType) {
    case '8':
      additionClassName = 'crossBox';
      break;
    case '4':
      additionClassName = 'checkBox';
      break;
    case 'n':
      additionClassName = 'squareBox';
      break;
    case 'l':
      additionClassName = 'circleBox';
      break;
    default:
      additionClassName = 'checkBox';
  }
  return additionClassName;
};

const allStyleKeys = ['borderColor', 'backgroundColor', 'color'];
const [borderColorKey, ...styleKeys] = allStyleKeys;

const NAMESPACES = ['ELEMENTS_SETTINGS_TOOLBAR', 'FILLABLE_ELEMENTS_MAIN_CONTENT'];

export const CheckboxGroup = ({ element, editable, selected, updateElementRect }) => {
  const { contents, style } = element;

  const [picker, setPicker] = useState(false);
  const { updateComplexElementDetails } = useContext(PageElementsContext);
  const { t } = useTranslation(NAMESPACES);

  const [, updateArtboard] = useArtboardContext(() => null);
  useEffect(() => {
    // close the sidebar when the element is removed from the block.
    return () => {
      updateArtboard({ [CONTEXT_KEYS.ELEMENT_SETTINGS_VISIBLE]: false });
    };
  }, []);

  const debouncedUpdateComplexElementDetails = useRef(
    debounce(updateComplexElementDetails, TEXT_DEBOUNCE_TIME_OUT),
  ).current;

  const updateElementDetails = useCallback(
    (value, key) => {
      const details = deepCopy(contents);
      if (styleKeys.includes(key)) {
        details.style = { ...details.style, [key]: value };
      } else if (key === borderColorKey) {
        details.style = { ...details.style, border: { [key]: value } };
      } else {
        details.formOptions = { ...details.formOptions, [key]: value };
      }
      updateComplexElementDetails(element.uuid, { contents: details }, 'form-options');
    },
    [updateComplexElementDetails, element, contents],
  );

  const updateElementStyles = useCallback(
    values => {
      const details = deepCopy(contents);
      details.style = { ...details.style, ...values };
      updateComplexElementDetails(element.uuid, { contents: details }, 'form-options');
    },
    [updateComplexElementDetails, element, contents],
  );

  const updateTextStyle = useCallback(
    values => {
      const details = { style: { ...values } };
      updateComplexElementDetails(element.uuid, details, 'form-options');
    },
    [element.uuid, updateComplexElementDetails],
  );

  const onGroupNameChange = useCallback(value => {
    const details = deepCopy(contents);
    details.formOptions = { ...details.formOptions, groupName: value };
    debouncedUpdateComplexElementDetails(element.uuid, { contents: details }, 'form-options');
  }, []);

  const showBackgroundColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'backgroundColor') {
        return false;
      }
      return { type: 'backgroundColor', color: contents.style.backgroundColor };
    });
  }, [contents]);

  const showBorderColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'borderColor') {
        return false;
      }
      return { type: 'borderColor', color: contents.style.border?.borderColor };
    });
  }, [contents]);

  const showElementColorPicker = useCallback(() => {
    setPicker(currentValue => {
      if (currentValue && currentValue.type === 'color') {
        return false;
      }
      return { type: 'color', color: contents.style.border?.borderColor };
    });
  }, [contents]);

  const onColorPick = useCallback(
    color => {
      if (picker.type === 'backgroundColor') {
        updateElementDetails(color.hex, 'backgroundColor');
        setPicker({ type: 'backgroundColor', color: color.hex });
      } else if (picker.type === 'borderColor') {
        updateElementDetails(color.hex, 'borderColor');
        setPicker({ type: 'borderColor', color: color.hex });
      } else if (picker.type === 'color') {
        updateElementDetails(color.hex, 'color');
        setPicker({ type: 'color', color: color.hex });
      }
    },
    [picker, updateElementDetails],
  );

  const textStyle = {
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    color: style.color,
    backgroundColor: style.backgroundColor,
    textDecoration: style.fontWeight?.includes('underline') ? 'underline' : '',
    fontWeight: style.fontWeight?.includes('bold') ? 'bold' : '',
    fontStyle: style.fontWeight?.includes('italics') ? 'italic' : '',
  };

  return (
    <CheckboxGroupSection
      borderColor={contents.style?.border?.borderColor}
      backgroundColor={contents.style?.backgroundColor}
      color={contents.style?.color}
      direction={contents?.formOptions?.direction}
      style={{ pointerEvents: !editable && 'none' }}
      className={`complex-documents ${getCheckBoxClassName(contents?.formOptions?.checkStyle)}`}
    >
      {contents?.formOptions?.direction === 'vertical' ? (
        <>
          <CheckboxWrapper width={contents.style.width} height={contents.style.height}>
            <Checkbox checked={true} />
            <span style={textStyle}>Check box</span>
          </CheckboxWrapper>
          <CheckboxWrapper width={contents.style.width} height={contents.style.height}>
            <Checkbox checked={true} />
            <span style={textStyle}>Check box</span>
          </CheckboxWrapper>{' '}
        </>
      ) : (
        <HorizontalLayout width={contents.style.width} height={contents.style.height}>
          <Checkbox checked={true} />
          <span style={textStyle}>Check box</span>
          <Checkbox checked={true} />
          <span style={textStyle}>Check box</span>
        </HorizontalLayout>
      )}
      {editable &&
        selected &&
        ReactDOM.createPortal(
          <CheckboxGroupSettings
            element={element}
            updateElementDetails={updateElementDetails}
            onGroupNameChange={onGroupNameChange}
            updateTextStyle={updateTextStyle}
            updateElementStyles={updateElementStyles}
            selected={selected}
            updateElementRect={updateElementRect}
          />,
          document.getElementById('sidebar-absolute-container'),
        )}
      {editable &&
        selected &&
        ReactDOM.createPortal(
          <FillableElementWrapper>
            <Tooltip
              placement="bottom"
              title={t(
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.key,
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BACKGROUND_TOOLTIP.value,
              )}
            >
              <ColorButton
                style={{ backgroundColor: contents.style.backgroundColor, position: 'relative' }}
                onClick={showBackgroundColorPicker}
              />
            </Tooltip>
            <Tooltip
              placement="bottom"
              title={t(
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.key,
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_BORDER_COLOR_TOOLTIP.value,
              )}
            >
              <ColorButton
                style={{
                  backgroundColor: contents.style.border?.borderColor,
                  position: 'relative',
                }}
                onClick={showBorderColorPicker}
              />
            </Tooltip>
            <Tooltip
              placement="bottom"
              title={t(
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_COLOR_TOOLTIP.key,
                ELEMENTS_SETTINGS_TOOLBAR.ELEMENT_COLOR_TOOLTIP.value,
              )}
            >
              <ColorButton
                style={{ backgroundColor: contents.style.color, position: 'relative' }}
                onClick={showElementColorPicker}
              />
            </Tooltip>
            {picker && (
              <div style={{ position: 'absolute', top: '100%', height: '366px' }}>
                <ColorPicker
                  onChange={onColorPick}
                  onBlur={() => setPicker(false)}
                  color={picker.color}
                  qlEditor={true}
                />
              </div>
            )}
          </FillableElementWrapper>,
          document.getElementById('document-editor-toolbar'),
        )}
    </CheckboxGroupSection>
  );
};
